import { ActionType } from '@editor/src/js/constants/common-action-type';
import { IMenuEventParams } from '@src/slide/application/service/menu-manager/menu-event/menu-event.interface';
import { mobxSet } from '@src/slide/infrastructure/module/independent/assert/get-set';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { ADDON_PREFIX } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import { TDWActionType, TDWModuleType, TDWOperationType, TDWVer9Type } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import {
    SUPERSCRIPT_DEFAULT_BASELINE_OFFSET, SUBSCRIPT_DEFAULT_BASELINE_OFFSET,
    FONT_EFFECT_KEY, FONT_FILL_KEY, FONT_BORDER_KEY,
} from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { IAddonHandler } from '@src/slide/application/service/addon/addon-handler.interface';
import AttachmentUploader from '@src/js/common/ark-extensions/dc-components/attachment-uploader';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO } from '@src/slide/infrastructure/module/independent/unit';
import { getParagraphListWithNewFont } from '@src/slide/domain/ui-model/element-modeles/property/text/paragraph';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import type { ColumnDataEmu } from '@src/slide/application/service/column-dialog-manager/core/interface';
import {
    IndentType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';

function getAddonManager() {
    return (AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices)
        .viewableServiceLayer.addonManager;
}

/**
 * 事件列表（主要逻辑从model文件迁移而来）
 */
const menuEventMap = {
    [ActionType.FONTSIZE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-size', values, options);
    },
    [ActionType.FONTSIZE_UP](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-size-up', values, options);
    },
    [ActionType.FONTSIZE_DOWN](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-size-down', values, options);
    },
    [ActionType.FONT](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-family', values, options);
    },
    [ActionType.BOLD](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-bold', values, options);
    },
    [ActionType.ITALIC](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-italic', values, options);
    },
    [ActionType.UNDERLINE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-underline', values, options);
    },
    [ActionType.DELETELINE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-delete', values, options);
    },
    [ActionType.SUBSCRIPT](model: IShapeModel, values, options) {
        const value = values ? SUBSCRIPT_DEFAULT_BASELINE_OFFSET : 0;
        return model.editorInstance?.operations('text-baseline-offset', value, options);
    },
    [ActionType.SUPERSCRIPT](model: IShapeModel, values, options) {
        const value = values ? SUPERSCRIPT_DEFAULT_BASELINE_OFFSET : 0;
        return model.editorInstance?.operations('text-baseline-offset', value, options);
    },
    [ActionType.FORECOLOR](model: IShapeModel, values: ITDColor, options) {
        return model.editorInstance?.operations('font-color', values, options);
    },
    [ActionType.BACKCOLOR](model: IShapeModel, values: ITDColor, options) {
        return model.editorInstance?.operations('font-background', values, options);
    },
    [ActionType.VERTICAL_ALIGN](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-vertical-align', values, options);
    },
    [ActionType.HORIZONTAL_ALIGN](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-horizontal-align', values, options);
    },
    [ActionType.HORIZONTAL_LEFT](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-horizontal-align', values, options);
    },
    [ActionType.HORIZONTAL_CENTER](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-horizontal-align', values, options);
    },
    [ActionType.HORIZONTAL_RIGHT](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-horizontal-align', values, options);
    },
    [ActionType.HORIZONTAL_JUSTIFY](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-horizontal-align', values, options);
    },
    [ActionType.HORIZONTAL_DISTRIBUTE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('font-horizontal-align', values, options);
    },
    [ActionType.UPDATE_PARAGRAPH_PROPS](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('paragraph-props', values, options);
    },
    [ActionType.ROW_SPACE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('paragraph-props', {
            PPTLineHeight: values,
            lineHeight: values * PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO,
            lineHeightIs: 'percent',
        }, options);
    },
    [ActionType.ROW_SPACE_ABSOLUTE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('paragraph-props', {
            PPTLineHeight: null,
            lineHeight: values,
            lineHeightType: 'absolute',
        }, options);
    },
    [ActionType.MARGIN_LEFT](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('margin-left', values, options);
    },
    [ActionType.TEXT_INDENT](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('text-indent', values, options);
    },
    [ActionType.SPACE_BEFORE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('space-before', values, options);
    },
    [ActionType.SPACE_AFTER](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('space-after', values, options);
    },
    [ActionType.TAB_SIZE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('tab-size', values, options);
    },
    [ActionType.TAB_LIST](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('tab-list', values, options);
    },
    [ActionType.ITEMNUMBER](model: IShapeModel, value, options) {
        return model.editorInstance?.operations(ActionType.BULLET_SET, { type: value }, options);
    },
    [ActionType.DIGITNUMBER](model: IShapeModel, value, options) {
        const menuValues = model.getMenuManager()?.toolbarMenu()?.values || {};
        // 获取当前选中元素的 startNumber，在这个基础上设置数字编号
        const startNumber = menuValues[ActionType.BULLET_START_NUMBER] || 1;
        return model.editorInstance?.operations(ActionType.BULLET_SET, { type: value, startNumber }, options);
    },
    [ActionType.BULLET_SET](model: IShapeModel, values, options) {
        return model.editorInstance?.operations(ActionType.BULLET_SET, values, options);
    },
    [ActionType.INDENT](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('indent', values, options);
    },
    [IndentType.INDENT_INCREASE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('indent', values, options);
    },
    [IndentType.INDENT_REDUCE](model: IShapeModel, values, options) {
        return model.editorInstance?.operations('indent', values, options);
    },
    [ActionType.FONT_EFFECT](model: IShapeModel, values, options) {
        return model.editorInstance?.operations(FONT_EFFECT_KEY, values, options);
    },
    [ActionType.TEXT_DIRECTION](model: IShapeModel, value: TextDirectionType, option) {
        return model.editorInstance?.operations(ActionType.TEXT_DIRECTION, value, option);
    },
    [ActionType.FONT_REFRESH](model: IShapeModel) {
        // 并不是菜单事件（服务于菜单事件，部分场景比如预览来恢复原文本值用的）
        return model.editorInstance?.refreshEditorByProps();
    },
    [ActionType.FONT_FILL](model: IShapeModel, values, options) {
        return model.editorInstance?.operations(FONT_FILL_KEY, values, options);
    },
    [ActionType.FONT_BORDER](model: IShapeModel, values, options) {
        return model.editorInstance?.operations(FONT_BORDER_KEY, values, options);
    },
    [ActionType.LINK](model: IShapeModel) {
        return model.editorInstance?.setTextLink();
    },
    [ActionType.ENTER_EDITOR](model: IShapeModel) {
        if (model.editorInstance) {
            // @ts-ignore
            model.editorInstance.setFocusOrClick?.();
        } else {
            mobxSet(self, 'editorEnterFocus', true);
        }
    },
    [ActionType.UNDO](model: IShapeModel) {
        model.editorInstance?.triggerUndo();
    },
    [ActionType.REDO](model: IShapeModel) {
        model.editorInstance?.triggerRedo();
    },
    [ActionType.TEXT_ADJUST](model: IShapeModel, values) {
        model.editorInstance?.triggerBlur();
        return model.updateTextAdjust(values);
    },
    [ActionType.PADDING_ADJUST](model: IShapeModel, values) {
        model.editorInstance?.triggerBlur();
        return model.updatePaddingAdjust(values);
    },
    // 删除链接
    [ActionType.REMOVE_LINK]: (model: IShapeModel) => {
        model.getLink().removeLink();
    },
    // 编辑链接
    [ActionType.EDIT_LINK]: (model: IShapeModel, values) => {
        model.getLink().updateLink({ href: values });
    },
    // 下载文件
    [ActionType.DOWNLOAD_ATTACHMENT]: (model: IShapeModel) => {
        if (model.isAttachment()) {
            const { objKey } = model.props.attachment;

            AttachmentUploader.getInstance().download(objKey);

            // 上报
            tdw({
                opername: TDWOperationType.DOC_WORD_EDITOR,
                module: TDWModuleType.LOCAL_FILES,
                action: TDWActionType.DOWNLOAD_CLICK,
            });
        }
    },
    // 图片校正
    [ActionType.REGULATE]: (model: IShapeModel, values, options) => {
        model.getImageFillEffect?.()?.handleImageEffect(values, options);
    },
    // 图片效果
    [ActionType.PICTURE_COLOR]: (model: IShapeModel, values, options) => {
        model.getImageFillEffect?.()?.handleImageEffect(values, options);
    },
    // 图片透明度
    [ActionType.INSERT_PICTURE]: (model: IShapeModel, values, options) => {
        model.getImageFillEffect?.()?.handleImageAlpha(values, options);
    },
    [ActionType.UNIFORM_FONT]: (model: IShapeModel, values) => {
        const newP = getParagraphListWithNewFont(model.paragraphList, values);
        model.updateParagraphListForUI(newP);
        model.updateParagraphList(newP);
    },
    [ActionType.COLUMNS](model: IShapeModel, values: ColumnDataEmu, options) {
        return model.editorInstance?.operations(ActionType.COLUMNS, values, options);
    },
};

/**
 * 事件响应匹配
 * 返回boolean值，表示此事件是否已在model独有事件匹配上，匹配上返回TRUE，结束匹配，未匹配上返回FALSE，外层继续匹配通用事件
 * @param model 当前uiModel
 * @param menuEventParams 菜单传参
 */
export function handleMenuEvent(model: IShapeModel, menuEventParams: IMenuEventParams): boolean {
    const { actionType, values, extraOption } = menuEventParams;
    const isNoEditingUndoRedo = [ActionType.UNDO, ActionType.REDO].includes(actionType)
        && !model.editorInstance?.isEditing;
    // 1.非编辑态的undo redo不走model内部逻辑，走通用undo redo
    if (isNoEditingUndoRedo) {
        return false;
    }
    // 精准查找事件
    if (menuEventMap[actionType]) {
        // @ts-ignore
        const options = { sync: extraOption?.isMultiGroup ?? false, needUpdate: extraOption?.needUpdate ?? false };
        menuEventMap[actionType](model, values, { ...extraOption, ...options });
        // 编辑链接弹框的逻辑奇葩，此处先维持原样不动
        if (ActionType.LINK === actionType) {
            return false;
        }
        return true;
    }
    // 范围匹配事件（插件）
    if (actionType.startsWith(ADDON_PREFIX)) {
        (getAddonManager().addonHandler as IAddonHandler)
            .handleAddonSceneItemClick(actionType, { ver9: TDWVer9Type.DETAIL_IFRAME_RIGHT_CLK });
        return true;
    }
    // 裁剪或裁剪为纵横比
    if (actionType.startsWith(ActionType.TAILOR_PIC) && !menuEventParams?.extraOption?.isMultiGroup) {
        // 范围匹配事件（裁剪）
        model.getImageCrop?.().handleCrop?.(actionType);
        return true;
    }
    // 在model独有的事件中没有匹配到，返回false，外层继续匹配通用事件
    return false;
}
